import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, query, where, onSnapshot, updateDoc, doc, arrayUnion,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { emojiPool } from '../services/themes';

// Host-side hook: subscribes to pending join requests for the current game,
// exposes accept/decline. accept() needs the current player list passed in
// because adding a new player requires writing the full updated array.
export default function useJoinRequests(gameId) {
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]);
  const unsubRef = useRef(null);

  useEffect(() => {
    if (!gameId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPendingJoinRequests([]);
      return;
    }
    const q = query(
      collection(db, 'joinRequests'),
      where('gameId', '==', gameId),
      where('status', '==', 'pending'),
    );
    unsubRef.current = onSnapshot(q, (snap) => {
      const reqs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort newest first for the banner display
      reqs.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt || 0);
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt || 0);
        return tb - ta;
      });
      setPendingJoinRequests(reqs);
    });
    return () => {
      if (unsubRef.current) unsubRef.current();
    };
  }, [gameId]);

  const acceptJoinRequest = useCallback(async (request, currentPlayers) => {
    if (!gameId || !request) return false;
    // Bail if this user already has a slot (race / double-tap)
    if (currentPlayers.some(p => p.authUid === request.fromAuthUid)) {
      await updateDoc(doc(db, 'joinRequests', request.id), { status: 'accepted' });
      return true;
    }
    const newSlot = {
      id: request.fromAuthUid,
      name: request.fromName,
      avatar: emojiPool[currentPlayers.length % emojiPool.length],
      score: 0,
      lastTurn: 'Joined!',
      userId: request.fromUserId || null,
      authUid: request.fromAuthUid,
      isRemote: true,
    };
    try {
      await updateDoc(doc(db, 'games', gameId), {
        players: [...currentPlayers, newSlot],
        playerIds: arrayUnion(request.fromAuthUid),
      });
      await updateDoc(doc(db, 'joinRequests', request.id), { status: 'accepted' });
      return true;
    } catch (e) {
      console.error('Could not accept join request:', e);
      return false;
    }
  }, [gameId]);

  const declineJoinRequest = useCallback(async (request) => {
    if (!request) return false;
    try {
      await updateDoc(doc(db, 'joinRequests', request.id), { status: 'declined' });
      return true;
    } catch (e) {
      console.error('Could not decline join request:', e);
      return false;
    }
  }, []);

  return { pendingJoinRequests, acceptJoinRequest, declineJoinRequest };
}
